import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
  AppBar, Toolbar, Box, Typography, IconButton, Button, Menu, MenuItem,
  Avatar, Badge, Chip, Divider, ListItemIcon, useTheme, useMediaQuery,
  Tooltip, alpha, Drawer, List, ListItem, ListItemButton, ListItemText,
  InputBase, styled
} from '@mui/material';
import {
  Dashboard, PointOfSale, TableBar, RestaurantMenu, Kitchen,
  ReceiptLong, Inventory, AccountBalance, Assessment, Group,
  CloudUpload, Settings, Logout, Menu as MenuIconOpen, Close,
  Search, Refresh, Store, Notifications, Security,
  KeyboardArrowDown as ArrowDownIcon, AccessTime, CalendarToday,
  MoreHoriz as MoreIcon,
} from '@mui/icons-material';

// ==================== STYLED COMPONENTS (RAATH DINE Theme) ====================

const SearchBar = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: 20,
  backgroundColor: alpha(theme.palette.common.white, 0.12),
  '&:hover': { backgroundColor: alpha(theme.palette.common.white, 0.2) },
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
  width: '100%',
  maxWidth: 360,
  border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
  transition: 'all 0.3s ease',
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: alpha(theme.palette.common.white, 0.6),
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    width: '100%',
    fontSize: '0.9rem',
    '&::placeholder': { color: alpha(theme.palette.common.white, 0.5), opacity: 1 },
  },
}));

// ==================== PATH PERMISSION MAP ====================
const PATH_PERMISSION_MAP = {
  '/': 'dashboard',
  '/dashboard': 'dashboard',
  '/billing': 'pos',
  '/pos': 'pos',
  '/tables': 'tables',
  '/menu': 'manage_menu',
  '/kitchen': 'kitchen',
  '/orders': 'orders',
  '/inventory': 'manage_inventory',
  '/accounts': 'view_accounts',
  '/reports': 'view_reports',
  '/staff': 'manage_staff',
  '/settings': 'manage_settings',
  '/backup': 'manage_backup',
};

// ==================== UNIFIED NAV STRUCTURE (RAATH DINE) ====================
const NAV_GROUPS = [
  {
    key: 'operations',
    label: 'Operations',
    icon: <PointOfSale fontSize="small" />,
    items: [
      { id: 'pos', label: 'New Bill', icon: <PointOfSale fontSize="small" />, path: '/billing' },
      { id: 'tables', label: 'Tables', icon: <TableBar fontSize="small" />, path: '/tables' },
      { id: 'orders', label: 'Orders', icon: <ReceiptLong fontSize="small" />, path: '/orders' },
    ]
  },
  {
    key: 'management',
    label: 'Management',
    icon: <RestaurantMenu fontSize="small" />,
    items: [
      { id: 'manage_menu', label: 'Menu', icon: <RestaurantMenu fontSize="small" />, path: '/menu' },
      { id: 'kitchen', label: 'Kitchen', icon: <Kitchen fontSize="small" />, path: '/kitchen' },
      { id: 'manage_inventory', label: 'Inventory', icon: <Inventory fontSize="small" />, path: '/inventory' },
    ]
  },
  {
    key: 'finance',
    label: 'Finance',
    icon: <AccountBalance fontSize="small" />,
    items: [
      { id: 'view_accounts', label: 'Accounts', icon: <AccountBalance fontSize="small" />, path: '/accounts' },
      { id: 'view_reports', label: 'Reports', icon: <Assessment fontSize="small" />, path: '/reports' },
    ]
  },
  {
    key: 'system',
    label: 'System',
    icon: <Settings fontSize="small" />,
    items: [
      { id: 'manage_staff', label: 'Staff', icon: <Group fontSize="small" />, path: '/staff' },
      { id: 'manage_backup', label: 'Backup', icon: <CloudUpload fontSize="small" />, path: '/backup' },
      { id: 'manage_settings', label: 'Settings', icon: <Settings fontSize="small" />, path: '/settings' },
    ]
  },
];

const allPaths = [
  { path: '/', label: 'Dashboard' },
  { path: '/dashboard', label: 'Dashboard' },
  ...NAV_GROUPS.flatMap(g => g.items),
];

// ==================== MAIN COMPONENT ====================
export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    logout,
    user,
    canAccessPage,
    getRoleColor,
    getRoleLabel,
    isAdmin
  } = useAuth();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const [anchors, setAnchors] = useState({
    operations: null, management: null, finance: null, system: null,
    profile: null, notif: null, more: null,
  });

  const [pendingAlerts, setPendingAlerts] = useState(3);
  const [currentTime, setCurrentTime] = useState('');
  const [currentDate, setCurrentDate] = useState('');

  // ===== EFFECTS =====
  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }));
      setCurrentDate(now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }));
    };
    updateDateTime();
    const interval = setInterval(updateDateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // ===== USER DATA =====
  const userName = user?.name || 'Guest';
  const userEmail = user?.email || '';
  const userRole = user?.role || '';

  const roleColorValue = getRoleColor();
  const roleLabelValue = getRoleLabel();

  const getInitials = () => {
    if (!userName || userName === 'Guest') return 'G';
    const names = userName.split(' ');
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  // ===== PERMISSIONS =====
  const canAccess = useCallback((permId) => {
    if (!user) return false;
    if (isAdmin) return true;
    return canAccessPage(permId);
  }, [canAccessPage, user, isAdmin]);

  // ===== HELPERS =====
  const isActive = useCallback((path) => location.pathname === path, [location.pathname]);
  const isGroupActive = useCallback((items) => items.some(item => isActive(item.path)), [isActive]);

  const openMenu = useCallback((key, event) => {
    setAnchors(prev => ({ ...prev, [key]: event.currentTarget }));
  }, []);

  const closeMenu = useCallback((key) => {
    setAnchors(prev => ({ ...prev, [key]: null }));
  }, []);

  const handleLogout = useCallback(() => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
      navigate('/login');
    }
    closeMenu('profile');
    setMobileDrawerOpen(false);
  }, [logout, closeMenu, navigate]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try { window.location.reload(); }
    catch (e) { console.error('Refresh error:', e); setRefreshing(false); }
  }, []);

  // ===== FILTERED GROUPS =====
  const filteredGroups = useMemo(() => {
    return NAV_GROUPS.map(group => ({
      ...group,
      items: group.items.filter(item => canAccess(item.id))
    })).filter(group => group.items.length > 0);
  }, [canAccess]);

  const currentPage = useMemo(() =>
    allPaths.find(p => isActive(p.path))?.label || 'Dashboard',
  [isActive]);

  const visibleGroups = useMemo(() => {
    if (isDesktop) return filteredGroups;
    return filteredGroups.slice(0, 3);
  }, [filteredGroups, isDesktop]);

  const moreGroups = useMemo(() => {
    if (isDesktop) return [];
    return filteredGroups.slice(3);
  }, [filteredGroups, isDesktop]);

  // ===== RENDER DROPDOWN =====
  const renderDropdown = useCallback((group) => {
    if (group.items.length === 0) return null;
    const key = group.key;
    const active = isGroupActive(group.items);
    return (
      <React.Fragment key={key}>
        <Button
          onClick={(e) => openMenu(key, e)}
          endIcon={<ArrowDownIcon fontSize="small" sx={{ color: active ? '#fff' : alpha('#fff', 0.6) }} />}
          startIcon={group.icon}
          size="small"
          sx={{
            color: active ? '#fff' : alpha('#fff', 0.8),
            bgcolor: active ? alpha('#fff', 0.15) : 'transparent',
            fontWeight: active ? 700 : 500,
            px: 1.5, py: 0.6, borderRadius: 2, textTransform: 'none', fontSize: '0.8rem',
            '&:hover': { bgcolor: active ? alpha('#fff', 0.2) : alpha('#fff', 0.1) },
            transition: 'all 0.2s',
          }}
        >
          {group.label}
        </Button>
        <Menu
          anchorEl={anchors[key]}
          open={Boolean(anchors[key])}
          onClose={() => closeMenu(key)}
          slotProps={{ paper: { sx: {
            width: 220, mt: 1.5, borderRadius: 2,
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)', py: 1,
            bgcolor: '#1a237e',
            border: `1px solid ${alpha('#fff', 0.1)}`,
          }}}}
          transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        >
          {group.items.map((item) => (
            <MenuItem
              key={item.path}
              component={Link}
              to={item.path}
              onClick={() => closeMenu(key)}
              selected={isActive(item.path)}
              sx={{
                py: 0.9, px: 2, borderRadius: 1, mx: 1, mb: 0.3,
                color: isActive(item.path) ? '#fff' : alpha('#fff', 0.8),
                '&:hover': { bgcolor: alpha('#fff', 0.1) },
                '&.Mui-selected': { bgcolor: alpha('#82b1ff', 0.2), color: '#82b1ff', fontWeight: 600 },
              }}
            >
              <ListItemIcon sx={{ color: isActive(item.path) ? '#82b1ff' : alpha('#fff', 0.5), minWidth: 32 }}>
                {item.icon}
              </ListItemIcon>
              <Typography variant="body2" fontWeight={isActive(item.path) ? 600 : 400}>
                {item.label}
              </Typography>
            </MenuItem>
          ))}
        </Menu>
      </React.Fragment>
    );
  }, [anchors, openMenu, closeMenu, isActive, isGroupActive]);

  // ===== MOBILE DRAWER CONTENT =====
  const mobileDrawerContent = useMemo(() => (
    <Box sx={{ width: 300, bgcolor: '#1a237e', background: 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)', height: '100%' }}>
      {/* Header */}
      <Box sx={{ p: 2.5, bgcolor: '#1a237e', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Store fontSize="large" sx={{ color: '#82b1ff' }} />
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>RAATH</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>DINE POS</Typography>
          </Box>
        </Box>
        <IconButton onClick={() => setMobileDrawerOpen(false)} sx={{ color: 'inherit' }}>
          <Close />
        </IconButton>
      </Box>

      {/* User */}
      {user && (
        <Box sx={{ p: 2, bgcolor: alpha('#1a237e', 0.3), borderBottom: `1px solid ${alpha('#fff', 0.1)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{
              width: 40, height: 40,
              bgcolor: roleColorValue,
              fontSize: '1rem',
              fontWeight: 'bold'
            }}>
              {getInitials()}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight="bold" sx={{ color: '#fff' }}>{userName}</Typography>
              <Chip
                size="small"
                label={roleLabelValue}
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  bgcolor: alpha(roleColorValue, 0.2),
                  color: roleColorValue,
                  fontWeight: 600,
                  border: `1px solid ${alpha(roleColorValue, 0.3)}`
                }}
              />
            </Box>
          </Box>
        </Box>
      )}

      {/* Quick Bill */}
      <Box sx={{ p: 2, pb: 0 }}>
        {canAccess('pos') && (
          <Button
            fullWidth
            variant="contained"
            color="success"
            component={Link}
            to="/billing"
            onClick={() => setMobileDrawerOpen(false)}
            startIcon={<PointOfSale />}
            sx={{ mb: 1, py: 1.2, fontWeight: 'bold', borderRadius: 2 }}
          >
            New Bill
          </Button>
        )}
      </Box>

      {/* Navigation */}
      <List dense sx={{ pt: 1 }}>
        {/* Dashboard */}
        {canAccess('dashboard') && (
          <ListItem disablePadding>
            <ListItemButton
              component={Link} to="/"
              onClick={() => setMobileDrawerOpen(false)}
              selected={isActive('/')}
              sx={{
                borderRadius: 1, mx: 1, mb: 0.5,
                '&.Mui-selected': { bgcolor: alpha('#82b1ff', 0.2), color: '#82b1ff' },
              }}
            >
              <ListItemIcon sx={{ color: isActive('/') ? '#82b1ff' : alpha('#fff', 0.5), minWidth: 36 }}>
                <Dashboard fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Dashboard"
                slotProps={{ primary: { sx: { fontSize: '14px', color: isActive('/') ? '#fff' : alpha('#fff', 0.7) } } }}
              />
            </ListItemButton>
          </ListItem>
        )}

        {/* Groups */}
        {filteredGroups.map((group, gIdx) => (
          <React.Fragment key={group.key}>
            {gIdx > 0 && <Divider sx={{ my: 1, borderColor: alpha('#fff', 0.1) }} />}
            <Typography variant="caption" sx={{
              px: 2, pt: 1, pb: 0.5, display: 'block',
              color: alpha('#fff', 0.4), fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.5px',
            }}>
              {group.label}
            </Typography>
            {group.items.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  onClick={() => setMobileDrawerOpen(false)}
                  selected={isActive(item.path)}
                  sx={{
                    borderRadius: 1, mx: 1, mb: 0.5,
                    '&.Mui-selected': { bgcolor: alpha('#82b1ff', 0.2), color: '#82b1ff' },
                  }}
                >
                  <ListItemIcon sx={{ color: isActive(item.path) ? '#82b1ff' : alpha('#fff', 0.5), minWidth: 36 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    slotProps={{ primary: { sx: { fontSize: '14px', color: isActive(item.path) ? '#fff' : alpha('#fff', 0.7) } } }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </React.Fragment>
        ))}
      </List>

      {/* Footer */}
      <Box sx={{ mt: 'auto', p: 2, borderTop: `1px solid ${alpha('#fff', 0.1)}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <AccessTime sx={{ fontSize: 12, color: alpha('#fff', 0.5) }} />
            <Typography sx={{ color: alpha('#fff', 0.6), fontSize: '0.65rem' }}>{currentTime}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <CalendarToday sx={{ fontSize: 12, color: alpha('#fff', 0.5) }} />
            <Typography sx={{ color: alpha('#fff', 0.6), fontSize: '0.65rem' }}>{currentDate}</Typography>
          </Box>
        </Box>
        <Button
          fullWidth
          onClick={handleLogout}
          sx={{
            color: '#f87171',
            borderRadius: 2,
            py: 1, fontSize: '0.85rem', fontWeight: 600, textTransform: 'none',
            border: `1px solid ${alpha('#f87171', 0.2)}`,
            '&:hover': { bgcolor: alpha('#f87171', 0.1) },
          }}
          startIcon={<Logout sx={{ fontSize: 18 }} />}
        >
          Logout
        </Button>
      </Box>
    </Box>
  ), [user, userRole, userName, userEmail, canAccess, isActive, filteredGroups, currentTime, currentDate, handleLogout, roleColorValue, roleLabelValue]);

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: '#1a237e',
          background: 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)',
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: 64, px: { xs: 1, sm: 2 } }}>
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileDrawerOpen(true)}
              sx={{ mr: 1.5, '&:hover': { bgcolor: alpha('#fff', 0.1) } }}
            >
              <MenuIconOpen />
            </IconButton>
          )}

          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: { xs: 1, md: 3 } }}>
            <Store sx={{ fontSize: 30, mr: 1, color: '#82b1ff' }} />
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="h6" sx={{
                fontWeight: 800, lineHeight: 1.1, letterSpacing: '0.5px', fontSize: '1.25rem',
              }}>
                RAATH
              </Typography>
              <Typography variant="caption" sx={{
                opacity: 0.8, display: 'block', lineHeight: 1, fontSize: '0.65rem', letterSpacing: '1.5px',
              }}>
                DINE POS
              </Typography>
            </Box>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, justifyContent: 'center' }}>
              {/* Dashboard */}
              {canAccess('dashboard') && (
                <Button
                  component={Link}
                  to="/"
                  startIcon={<Dashboard fontSize="small" />}
                  size="small"
                  sx={{
                    color: isActive('/') ? '#fff' : alpha('#fff', 0.75),
                    bgcolor: isActive('/') ? alpha('#fff', 0.15) : 'transparent',
                    fontWeight: isActive('/') ? 700 : 500,
                    px: 1.5, py: 0.6, borderRadius: 2, textTransform: 'none', fontSize: '0.8rem',
                    '&:hover': { bgcolor: isActive('/') ? alpha('#fff', 0.2) : alpha('#fff', 0.1) },
                    transition: 'all 0.2s',
                  }}
                >
                  Dashboard
                </Button>
              )}

              {/* Groups */}
              {visibleGroups.map(group => renderDropdown(group))}

              {/* More Dropdown */}
              {moreGroups.length > 0 && (
                <React.Fragment>
                  <Button
                    onClick={(e) => openMenu('more', e)}
                    endIcon={<ArrowDownIcon fontSize="small" sx={{ color: alpha('#fff', 0.6) }} />}
                    startIcon={<MoreIcon fontSize="small" />}
                    size="small"
                    sx={{
                      color: alpha('#fff', 0.8),
                      fontWeight: 500,
                      px: 1.5, py: 0.6, borderRadius: 2, textTransform: 'none', fontSize: '0.8rem',
                      '&:hover': { bgcolor: alpha('#fff', 0.1) },
                      transition: 'all 0.2s',
                    }}
                  >
                    More
                  </Button>
                  <Menu
                    anchorEl={anchors.more}
                    open={Boolean(anchors.more)}
                    onClose={() => closeMenu('more')}
                    slotProps={{ paper: { sx: {
                      width: 220, mt: 1.5, borderRadius: 2,
                      boxShadow: '0 10px 30px rgba(0,0,0,0.15)', py: 1,
                      bgcolor: '#1a237e',
                      border: `1px solid ${alpha('#fff', 0.1)}`,
                    }}}}
                    transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                  >
                    {moreGroups.map((group) => (
                      <React.Fragment key={group.key}>
                        <Typography variant="caption" sx={{
                          px: 2, pt: 1, pb: 0.5, display: 'block',
                          color: alpha('#fff', 0.5), fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.5px',
                        }}>
                          {group.label}
                        </Typography>
                        {group.items.map((item) => (
                          <MenuItem
                            key={item.path}
                            component={Link}
                            to={item.path}
                            onClick={() => closeMenu('more')}
                            selected={isActive(item.path)}
                            sx={{
                              py: 0.9, px: 2, borderRadius: 1, mx: 1, mb: 0.3,
                              color: isActive(item.path) ? '#fff' : alpha('#fff', 0.8),
                              '&:hover': { bgcolor: alpha('#fff', 0.1) },
                              '&.Mui-selected': { bgcolor: alpha('#82b1ff', 0.2), color: '#82b1ff', fontWeight: 600 },
                            }}
                          >
                            <ListItemIcon sx={{ color: isActive(item.path) ? '#82b1ff' : alpha('#fff', 0.5), minWidth: 32 }}>
                              {item.icon}
                            </ListItemIcon>
                            <Typography variant="body2" fontWeight={isActive(item.path) ? 600 : 400}>
                              {item.label}
                            </Typography>
                          </MenuItem>
                        ))}
                        {group !== moreGroups[moreGroups.length - 1] && (
                          <Divider sx={{ borderColor: alpha('#fff', 0.1), my: 0.5, mx: 1 }} />
                        )}
                      </React.Fragment>
                    ))}
                  </Menu>
                </React.Fragment>
              )}
            </Box>
          )}

          {/* Search Bar */}
          {!isMobile && (
            <SearchBar>
              <SearchIconWrapper><Search fontSize="small" /></SearchIconWrapper>
              <StyledInputBase
                placeholder="Search menu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchBar>
          )}

          {/* Right Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
            {/* Quick Bill */}
            {!isMobile && canAccess('pos') && (
              <Tooltip title="Quick New Bill">
                <Button
                  variant="contained"
                  size="small"
                  component={Link}
                  to="/billing"
                  startIcon={<PointOfSale fontSize="small" />}
                  sx={{
                    mr: 1.5, bgcolor: '#00c853', fontWeight: 'bold', textTransform: 'none',
                    borderRadius: 2, px: 2,
                    boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    '&:hover': { bgcolor: '#00e676', boxShadow: '0 6px 16px rgba(0,200,83,0.4)', transform: 'translateY(-1px)' },
                    transition: 'all 0.2s',
                  }}
                >
                  Bill
                </Button>
              </Tooltip>
            )}

            {/* Time (Desktop) */}
            <Box sx={{ display: { xs: 'none', xl: 'flex' }, alignItems: 'center', gap: 0.8, mr: 1 }}>
              <AccessTime sx={{ fontSize: 14, color: alpha('#fff', 0.5) }} />
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.7), fontSize: '0.7rem', fontFamily: 'monospace' }}>
                {currentTime}
              </Typography>
            </Box>

            {/* Refresh */}
            <Tooltip title={refreshing ? "Refreshing..." : "Refresh App"}>
              <IconButton
                color="inherit"
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{
                  color: refreshing ? alpha('#fff', 0.4) : alpha('#fff', 0.8),
                  '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.1) },
                  animation: refreshing ? 'spin 1s linear infinite' : 'none',
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Notifications */}
            <Tooltip title="Notifications">
              <IconButton
                color="inherit"
                onClick={(e) => openMenu('notif', e)}
                sx={{ color: alpha('#fff', 0.8), '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.1) } }}
              >
                <Badge badgeContent={pendingAlerts} color="error">
                  <Notifications fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Avatar */}
            <Tooltip title={`${userName} • ${roleLabelValue || 'Guest'}`}>
              <IconButton onClick={(e) => openMenu('profile', e)} size="small" sx={{ ml: 0.5 }}>
                <Avatar sx={{
                  width: 34, height: 34,
                  bgcolor: roleColorValue || '#82b1ff',
                  color: '#fff',
                  fontSize: '0.9rem', fontWeight: 'bold',
                  border: '2px solid rgba(255,255,255,0.3)',
                }}>
                  {getInitials()}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>

        {/* Bottom Status Bar */}
        {!isMobile && (
          <Box sx={{
            px: 2, py: 0.4,
            bgcolor: alpha('#000', 0.15),
            borderTop: `1px solid ${alpha('#fff', 0.08)}`,
            display: 'flex', alignItems: 'center', gap: 2,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 500 }}>📍</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: '0.3px' }}>{currentPage}</Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ bgcolor: alpha('#fff', 0.15), height: 14 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#69f0ae' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#69f0ae' }}>Restaurant Open</Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ bgcolor: alpha('#fff', 0.15), height: 14 }} />
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {new Date().toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
            </Typography>
            {user && (
              <>
                <Divider orientation="vertical" flexItem sx={{ bgcolor: alpha('#fff', 0.15), height: 14 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Security fontSize="small" sx={{ fontSize: '0.8rem', color: roleColorValue || '#69f0ae' }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: roleColorValue || '#69f0ae' }}>
                    {roleLabelValue}
                  </Typography>
                </Box>
              </>
            )}
            <Box sx={{ ml: 'auto' }}>
              <Typography variant="caption" sx={{ opacity: 0.4, fontSize: '0.5rem', letterSpacing: 1 }}>
                v1.0.0
              </Typography>
            </Box>
          </Box>
        )}
      </AppBar>

      {/* Notifications Menu */}
      <Menu
        anchorEl={anchors.notif}
        open={Boolean(anchors.notif)}
        onClose={() => closeMenu('notif')}
        slotProps={{ paper: { sx: {
          width: 320, mt: 1.5, borderRadius: 2,
          boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
        }}}}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
          <Typography variant="subtitle2" fontWeight="bold">Notifications</Typography>
          <Typography variant="caption" color="text.secondary">{pendingAlerts} new alerts</Typography>
        </Box>
        {[
          { title: 'New Order', desc: 'Table 5 - Order #ORD-0042', time: '2 min ago', color: '#43a047' },
          { title: 'Payment Due', desc: 'Table 3 - PKR 4,500 pending', time: '15 min ago', color: '#fb8c00' },
          { title: 'Kitchen Alert', desc: 'Biryani - Ready for serving', time: '5 min ago', color: '#e53935' },
        ].map((notif, i) => (
          <MenuItem key={i} sx={{ py: 1.5, px: 2, alignItems: 'flex-start' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: notif.color, mt: 0.8, mr: 1.5, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.3 }}>{notif.title}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">{notif.desc}</Typography>
              <Typography variant="caption" color="primary" sx={{ fontSize: '0.7rem' }}>{notif.time}</Typography>
            </Box>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem sx={{ justifyContent: 'center', py: 1 }}>
          <Typography variant="caption" color="primary" fontWeight= "600">View All Notifications</Typography>
        </MenuItem>
      </Menu>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchors.profile}
        open={Boolean(anchors.profile)}
        onClose={() => closeMenu('profile')}
        slotProps={{ paper: { sx: {
          width: 240, mt: 1.5, borderRadius: 2,
          boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
        }}}}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Avatar sx={{
              width: 36, height: 36,
              bgcolor: roleColorValue || theme.palette.primary.main,
              fontSize: '0.9rem',
              fontWeight: 'bold',
              color: '#fff'
            }}>
              {getInitials()}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ lineHeight: 1.2 }}>{userName}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{userEmail}</Typography>
            </Box>
          </Box>
          <Chip
            size="small"
            label={roleLabelValue || 'Guest'}
            sx={{
              height: 20,
              fontSize: '0.65rem',
              mt: 0.5,
              bgcolor: alpha(roleColorValue, 0.15),
              color: roleColorValue,
              fontWeight: 600,
              border: `1px solid ${alpha(roleColorValue, 0.3)}`
            }}
          />
        </Box>
        <Divider />
        <MenuItem onClick={() => { closeMenu('profile'); navigate('/dashboard'); }} sx={{ py: 1 }}>
          <Dashboard fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
          <Typography variant="body2">Dashboard</Typography>
        </MenuItem>
        {canAccess('manage_settings') && (
          <MenuItem component={Link} to="/settings" onClick={() => closeMenu('profile')} sx={{ py: 1 }}>
            <Settings fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
            <Typography variant="body2">Settings</Typography>
          </MenuItem>
        )}
        <MenuItem onClick={() => { closeMenu('profile'); navigate('/backup'); }} sx={{ py: 1 }}>
          <CloudUpload fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
          <Typography variant="body2">Backup</Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ py: 1, color: 'error.main' }}>
          <Logout fontSize="small" sx={{ mr: 1.5, color: 'inherit' }} />
          <Typography variant="body2">Logout</Typography>
        </MenuItem>
      </Menu>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        slotProps={{ root: { keepMounted: true } }}
      >
        {mobileDrawerContent}
      </Drawer>

      {/* CSS Animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}